// src/pages/inventory/ManageWarehousesPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Warehouse, 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  MoreVertical,
  MapPin,
  Phone,
  AlertCircle,
  RefreshCw,
  Save,
  X,
  ArrowLeft,
  Users
} from 'lucide-react';
import { 
  getWarehouses, 
  createWarehouse, 
  getWarehouse, 
  updateWarehouse, 
  deleteWarehouse 
} from '../../services/api/warehouses';
import { usePermissions } from '../../hooks/usePermissions';
import { getMyUsage } from '../../services/api/subscription';
import QuotaWarning from '../../components/common/Subscription/QuotaWarning';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import styles from './ManageWarehousesPage.module.css';

const ManageWarehousesPage = () => {
  const { hasPermission } = usePermissions();
  const { t } = useLocalization();
  const { success, error } = useAlert();
  const navigate = useNavigate();
  
  // State management
  const [warehouses, setWarehouses] = useState([]);
  const [filteredWarehouses, setFilteredWarehouses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [quotaInfo, setQuotaInfo] = useState(null);
  const [isLoadingQuota, setIsLoadingQuota] = useState(true);
  
  // Modal/Form states
  const [showForm, setShowForm] = useState(false);
  const [editingWarehouse, setEditingWarehouse] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Load warehouses on component mount
  useEffect(() => {
    fetchWarehouses();
  }, []);

  // Filter warehouses when search query or warehouses change
  useEffect(() => {
    filterWarehouses();
  }, [warehouses, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setActiveDropdown(null);
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const fetchQuota = async () => {
      try {
        setIsLoadingQuota(true);
        const response = await getMyUsage();
        setQuotaInfo(response.usage?.warehouses || null);
      } catch (err) {
        console.error('Failed to fetch quota:', err);
      } finally {
        setIsLoadingQuota(false);
      }
    };

    fetchQuota();
  }, []);

  const fetchWarehouses = async () => {
    try {
      setIsLoading(true);
      setLoadingError(null);
      const response = await getWarehouses();
      setWarehouses(response);
    } catch (err) {
      console.error('Failed to fetch warehouses:', err);
      setLoadingError(t('errors.somethingWentWrong'));
    } finally {
      setIsLoading(false);
    }
  };

  const filterWarehouses = () => {
    if (!searchQuery.trim()) {
      setFilteredWarehouses(warehouses);
      return;
    }

    const filtered = warehouses.filter(warehouse =>
      warehouse.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (warehouse.address && warehouse.address.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (warehouse.phone && warehouse.phone.includes(searchQuery))
    );
    setFilteredWarehouses(filtered);
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = t('warehouses.nameRequired');
    } else if (formData.name.trim().length < 2) {
      errors.name = t('warehouses.nameMinLength');
    }

    if (formData.phone && !/^[0-9+\-\s()]+$/.test(formData.phone)) {
      errors.phone = t('warehouses.invalidPhone');
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const resetForm = () => {
    setFormData({ name: '', address: '', phone: '' });
    setFormErrors({});
    setEditingWarehouse(null);
  };

  const handleCreateNew = () => {
    resetForm();
    setShowForm(true);
  };

  const handleEdit = async (warehouseId) => {
    try {
      setIsSubmitting(true);
      const warehouse = await getWarehouse(warehouseId);
      setFormData({
        name: warehouse.name || '',
        address: warehouse.address || '',
        phone: warehouse.phone || ''
      });
      setEditingWarehouse(warehouse);
      setShowForm(true);
      setActiveDropdown(null);
    } catch (err) {
      console.error('Failed to fetch warehouse details:', err);
      error(t('warehouses.failedToLoadWarehouse'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (warehouseId, warehouseName) => {
    if (!window.confirm(t('warehouses.confirmDelete', { warehouseName }))) {
      return;
    }

    try {
      await deleteWarehouse(warehouseId);
      setWarehouses(prev => prev.filter(w => w.id !== warehouseId));
      success(t('warehouses.warehouseDeletedSuccess', { warehouseName }));
    } catch (err) {
      console.error('Failed to delete warehouse:', err);
      error(t('warehouses.failedToDelete'));
    }
    setActiveDropdown(null);
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setIsSubmitting(true);
    setFormErrors({});

    try {
      const submitData = {
        name: formData.name.trim(),
        address: formData.address.trim() || undefined,
        phone: formData.phone.trim() || undefined
      };

      if (editingWarehouse) {
        // Update existing warehouse
        const response = await updateWarehouse(editingWarehouse.id, submitData);
        setWarehouses(prev => 
          prev.map(w => w.id === editingWarehouse.id ? response.warehouse : w)
        );
        success(t('warehouses.warehouseUpdatedSuccess', { warehouseName: response.warehouse.name }));
      } else {
        // Create new warehouse
        const response = await createWarehouse(submitData);
        setWarehouses(prev => [...prev, response.warehouse]);
        success(t('warehouses.warehouseCreatedSuccess', { warehouseName: response.warehouse.name }));
      }

      setShowForm(false);
      resetForm();
    } catch (err) {
      console.error('Failed to save warehouse:', err);
      if (err.response?.data?.message) {
        setFormErrors({ submit: err.response.data.message });
      } else {
        setFormErrors({ submit: editingWarehouse ? t('warehouses.failedToUpdate') : t('warehouses.failedToCreate') });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    resetForm();
  };

  const toggleDropdown = (warehouseId) => {
    setActiveDropdown(activeDropdown === warehouseId ? null : warehouseId);
  };

  const getQuotaSeverity = () => {
    if (!quotaInfo || quotaInfo.isUnlimited) return 'info';
    const percentage = quotaInfo.limit > 0 ? (quotaInfo.usage / quotaInfo.limit) * 100 : 0;
    if (percentage >= 90) return 'critical';
    if (percentage >= 80) return 'warning';
    return 'info';
  };

  const isQuotaExceeded = () => {
    if (!quotaInfo || quotaInfo.isUnlimited) return false;
    return quotaInfo.usage >= quotaInfo.limit;
  };

  if (isLoading) {
    return (
      <div className={styles.manageWarehousesPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <Warehouse className={styles.headerIcon} size={24} />
              <div className={styles.titleContent}>
                <h1>{t('sidebar.warehouses')}</h1>
                <p>{t('warehouses.manageYourWarehouses')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.loadingState}>
          <div className={styles.loadingSpinnerLarge}></div>
          <h3>{t('warehouses.loadingWarehouses')}</h3>
          <p>{t('warehouses.loadingWarehousesDesc')}</p>
        </div>
      </div>
    );
  }

  if (loadingError) {
    return (
      <div className={styles.manageWarehousesPage}>
        <div className={styles.pageHeader}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <Warehouse className={styles.headerIcon} size={24} />
              <div className={styles.titleContent}>
                <h1>{t('sidebar.warehouses')}</h1>
                <p>{t('warehouses.manageYourWarehouses')}</p>
              </div>
            </div>
          </div>
        </div>

        <div className={styles.errorState}>
          <AlertCircle className={styles.errorIcon} size={48} />
          <h3>{t('errors.somethingWentWrong')}</h3>
          <p>{loadingError}</p>
          <button onClick={fetchWarehouses} className={styles.retryButton}>
            <RefreshCw size={16} />
            {t('common.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.manageWarehousesPage}>
      {/* Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Warehouse className={styles.headerIcon} size={24} />
            <div className={styles.titleContent}>
              <h1>{t('sidebar.warehouses')}</h1>
              <p>{t('warehouses.manageYourWarehouses')}</p>
            </div>
          </div>
          
          {hasPermission('create_warehouse') && (
            <div className={styles.headerActions}>
              <button 
                onClick={handleCreateNew} 
                className={styles.primaryButton}
                disabled={isQuotaExceeded()}
              >
                <Plus size={20} />
                {t('warehouses.addWarehouse')}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Controls */}
      <div className={styles.pageControls}>
        <div className={styles.searchSection}>
          <div className={styles.searchInputWrapper}>
            <Search className={styles.searchIcon} size={20} />
            <input
              type="text"
              placeholder={t('warehouses.searchWarehouses')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={styles.searchInput}
            />
          </div>
        </div>
        
        <div className={styles.controlsRight}>
          <div className={styles.resultsInfo}>
            {searchQuery ? (
              <span>{t('warehouses.filteredResults', { 
                count: filteredWarehouses.length,
                total: warehouses.length 
              })}</span>
            ) : (
              <span>{t('warehouses.totalWarehouses', { count: warehouses.length })}</span>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className={styles.pageContent}>
        {/* Quota Warning */}
        {!isLoadingQuota && quotaInfo && !showForm && (
          <QuotaWarning
            resourceType="warehouses"
            usage={quotaInfo}
            severity={getQuotaSeverity()}
            dismissible={!isQuotaExceeded()}
          />
        )}
        {filteredWarehouses.length === 0 ? (
          <div className={styles.emptyState}>
            {searchQuery ? (
              <>
                <Search className={styles.emptyIcon} size={48} />
                <h3>{t('warehouses.noSearchResults')}</h3>
                <p>{t('warehouses.noSearchResultsDesc')}</p>
                <button 
                  onClick={() => setSearchQuery('')} 
                  className={styles.secondaryButton}
                >
                  {t('common.clearSearch')}
                </button>
              </>
            ) : (
              <>
                <Warehouse className={styles.emptyIcon} size={48} />
                <h3>{t('warehouses.noWarehouses')}</h3>
                <p>{t('warehouses.noWarehousesDesc')}</p>
                {hasPermission('create_warehouse') && (
                  <button onClick={handleCreateNew} className={styles.primaryButton}>
                    <Plus size={20} />
                    {t('warehouses.createFirstWarehouse')}
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          <div className={styles.warehousesTableContainer}>
            <table className={styles.warehousesTable}>
              <thead>
                <tr>
                  <th className={styles.nameColumn}>{t('warehouses.warehouseName')}</th>
                  <th className={styles.addressColumn}>{t('warehouses.address')}</th>
                  <th className={styles.phoneColumn}>{t('warehouses.phone')}</th>
                  <th className={styles.actionsColumn}>{t('common.actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredWarehouses.map((warehouse) => (
                  <tr key={warehouse.id} className={styles.warehouseRow}>
                    <td className={styles.nameCell}>
                      <div className={styles.warehouseInfo}>
                        <Warehouse className={styles.warehouseIcon} size={16} />
                        <span className={styles.warehouseName}>{warehouse.name}</span>
                      </div>
                    </td>
                    
                    <td className={styles.addressCell}>
                      {warehouse.address ? (
                        <div className={styles.addressInfo}>
                          <MapPin size={12} />
                          <span>{warehouse.address}</span>
                        </div>
                      ) : (
                        <span className={styles.noData}>—</span>
                      )}
                    </td>
                    
                    <td className={styles.phoneCell}>
                      {warehouse.phone ? (
                        <div className={styles.phoneInfo}>
                          <Phone size={12} />
                          <span>{warehouse.phone}</span>
                        </div>
                      ) : (
                        <span className={styles.noData}>—</span>
                      )}
                    </td>
                    
                    <td className={styles.actionsCell}>
                      <div className={styles.actionsWrapper}>
                        {hasPermission('assign_users_to_warehouse') && (
                          <button
                            onClick={() => navigate(`/inventory/warehouses/${warehouse.id}/assign-users`)}
                            className={`${styles.actionButton} ${styles.assignUsersButton}`}
                            title="Assign Users"
                          >
                            <Users size={14} />
                          </button>
                        )}

                        {hasPermission('edit_warehouse') && (
                          <button
                            onClick={() => handleEdit(warehouse.id)}
                            className={`${styles.actionButton} ${styles.editButton}`}
                            title={t('common.edit')}
                          >
                            <Edit size={14} />
                          </button>
                        )}

                        {hasPermission('edit_warehouse') && (
                          <div className={styles.dropdownWrapper}>
                            <button
                              className={`${styles.actionButton} ${styles.moreButton}`}
                              onClick={() => toggleDropdown(warehouse.id)}
                              title={t('common.moreActions')}
                            >
                              <MoreVertical size={14} />
                            </button>

                            {activeDropdown === warehouse.id && (
                              <div className={styles.dropdownMenu}>
                                {hasPermission('assign_users_to_warehouse') && (
                                  <button
                                    className={styles.dropdownItem}
                                    onClick={() => navigate(`/inventory/warehouses/${warehouse.id}/assign-users`)}
                                  >
                                    <Users size={14} />
                                    Assign Users
                                  </button>
                                )}
                                <button
                                  className={`${styles.dropdownItem} ${styles.deleteItem}`}
                                  onClick={() => handleDelete(warehouse.id, warehouse.name)}
                                >
                                  <Trash2 size={14} />
                                  {t('common.delete')}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Form Modal */}
      {showForm && (
        <div className={styles.modalOverlay}>
          <div className={styles.modalContent}>
            <div className={styles.modalHeader}>
              <h2>
                {editingWarehouse ? t('warehouses.editWarehouse') : t('warehouses.createNewWarehouse')}
              </h2>
              <button onClick={handleCancelForm} className={styles.modalCloseButton}>
                <X size={20} />
              </button>
            </div>

            {formErrors.submit && (
              <div className={styles.errorBanner}>
                <AlertCircle size={20} />
                <p>{formErrors.submit}</p>
              </div>
            )}

            <form onSubmit={handleFormSubmit} className={styles.modalForm}>
              <div className={styles.formGroup}>
                <label htmlFor="name">{t('warehouses.warehouseName')} *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder={t('warehouses.warehouseNamePlaceholder')}
                  className={formErrors.name ? 'error' : ''}
                  required
                  autoFocus
                />
                {formErrors.name && <span className={styles.errorText}>{formErrors.name}</span>}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="address">{t('warehouses.address')}</label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  placeholder={t('warehouses.addressPlaceholder')}
                  rows="3"
                />
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="phone">{t('warehouses.phone')}</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder={t('warehouses.phonePlaceholder')}
                  className={formErrors.phone ? 'error' : ''}
                />
                {formErrors.phone && <span className={styles.errorText}>{formErrors.phone}</span>}
              </div>

              <div className={styles.modalActions}>
                <button 
                  type="button" 
                  onClick={handleCancelForm}
                  className={styles.cancelButton}
                >
                  <X size={18} />
                  {t('common.cancel')}
                </button>
                <button 
                  type="submit" 
                  className={styles.submitButton}
                  disabled={isSubmitting || isQuotaExceeded()}
                >
                  {isSubmitting ? (
                    <div className={styles.loadingSpinner}></div>
                  ) : (
                    <>
                      <Save size={18} />
                      {editingWarehouse ? t('warehouses.updateWarehouse') : t('warehouses.createWarehouse')}
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageWarehousesPage;